import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UseCardCarouselProps = {
	itemCount: number;
};

type UseCardCarouselReturn = {
	scrollContainerRef: React.RefObject<HTMLDivElement>;
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
	activeIndex: number;
	canScrollLeft: boolean;
	canScrollRight: boolean;
	handleScrollLeft: () => void;
	handleScrollRight: () => void;
	setItemRef: (index: number) => (el: HTMLDivElement | null) => void;
};

function findClosestItemIndex(
	container: HTMLDivElement,
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>,
): number {
	const containerCenter = container.scrollLeft + container.offsetWidth / 2;

	let closestIndex = 0;
	let smallestDistance = Infinity;

	itemRefs.current.forEach((el, index) => {
		if (!el) return;

		const itemCenter = el.offsetLeft + el.offsetWidth / 2;
		const distance = Math.abs(containerCenter - itemCenter);

		if (distance < smallestDistance) {
			smallestDistance = distance;
			closestIndex = index;
		}
	});

	return closestIndex;
}

function createScrollHandler(
	scrollTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
	updateActiveIndex: () => void,
): () => void {
	return () => {
		const timeoutRef = scrollTimeoutRef;
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			updateActiveIndex();
		}, 100);
	};
}

function setupScrollListener(
	container: HTMLDivElement,
	scrollTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
	updateActiveIndex: () => void,
) {
	const handleScroll = createScrollHandler(scrollTimeoutRef, updateActiveIndex);
	const timeoutRef = scrollTimeoutRef;

	container.addEventListener('scroll', handleScroll);
	updateActiveIndex();

	return () => {
		container.removeEventListener('scroll', handleScroll);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	};
}

function createScrollHandlers(activeIndex: number, itemCount: number, scrollToItem: (index: number) => void) {
	return {
		handleScrollLeft: () => {
			if (activeIndex > 0) {
				scrollToItem(activeIndex - 1);
			}
		},
		handleScrollRight: () => {
			if (activeIndex < itemCount - 1) {
				scrollToItem(activeIndex + 1);
			}
		},
	};
}

export function useCardCarousel({ itemCount }: UseCardCarouselProps): UseCardCarouselReturn {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const [activeIndex, setActiveIndex] = useState(0);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const updateActiveIndex = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container || container.clientWidth === 0) return;
		const closestIndex = findClosestItemIndex(container, itemRefs);
		setActiveIndex(closestIndex);
	}, []);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return undefined;
		return setupScrollListener(container, scrollTimeoutRef, updateActiveIndex);
	}, [itemCount, updateActiveIndex]);

	const scrollToItem = useCallback((index: number) => {
		const item = itemRefs.current[index];
		if (item && scrollContainerRef.current) {
			item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
		}
	}, []);

	const { handleScrollLeft, handleScrollRight } = useMemo(
		() => createScrollHandlers(activeIndex, itemCount, scrollToItem),
		[activeIndex, itemCount, scrollToItem],
	);

	const setItemRef = useCallback(
		(index: number) => (el: HTMLDivElement | null) => {
			itemRefs.current[index] = el;
		},
		[],
	);

	return {
		scrollContainerRef,
		itemRefs,
		activeIndex,
		canScrollLeft: activeIndex > 0,
		canScrollRight: activeIndex < itemCount - 1,
		handleScrollLeft,
		handleScrollRight,
		setItemRef,
	};
}
